import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import {updatePopUpStateAndPosition} from '@Components/poster-editor/components/poster-popup-menu/poster-popup-menu-slice';
import {onOpenReplaceAudio, onOpenReplaceBackground, onOpenReplaceMedia, openEditMenuModal, openEditTableContentModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {openLayersPanel} from '@Components/poster-editor/library/poster-editor-library';
import type {ReplaceMediaControlsButtons} from '@Components/replace-media-controls';
import {TimelineItemType} from '@Components/poster-editor/components/timeline/timeline.types';
import {splitTimelineItem, canSplitTimelineItem} from '@Components/poster-editor/components/timeline/utils/timeline-calculations';
import {getCurrentPageTime, getSelectedTimelineItem} from '@Components/poster-editor/poster-editor-reducer';
import {makeShortcut} from '@Utils/shortcut.util';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';
import {useSingleActivePosterItem} from '@Hooks/poster-editor/useSingleActivePosterItem';
import {getIsSpellCheckEnabledFromStore} from '@Libraries/spell-check-library';
import {useGetSelectedSlide} from '@Hooks/poster-editor/useGetSelectedSlide';
import {isUserPremium} from '@Libraries/user.library';
import {getVideoItemOrderInPage, showAiSubtitlesUpsellDialog} from '@Libraries/ai-transcript.library';
import {useAiTranscriptGeneration} from '@Hooks/poster-editor/useAiTranscriptGeneration';
import {GA4EventName, GA4EventParam, GA4EventParamName, LayersGA4Source, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import type {FabricPositionalParams} from '@Utils/fabric.util';
import {Canvas, Textbox} from '@postermywall/fabricjs-2';
import type * as Fabric from '@postermywall/fabricjs-2';
import {PopupSubmenu} from './components/popup-submenu/popup-submenu';
import {POPUP_SUBMENU_MARKER_ATTR, SUBMENU_HOVER_DELAY_MS} from './poster-popup-menu.types';
import {
  buildAlignSubMenuItems,
  buildAudioReplaceSubMenuItems,
  buildBackgroundReplaceSubMenuItems,
  buildItemsForAudioItem,
  buildItemsForBackgroundItem,
  buildItemsForCanvas,
  buildItemsForGroup,
  buildItemsForSingleItem,
  buildItemsForTextEdit,
  buildItemsForTimeline,
  buildPositionSubMenuItems,
  buildReplaceSubMenuItems,
} from './poster-popup-menu-items';
import type {PopupMenuCtx} from './poster-popup-menu-items';
import {
  copyFromText,
  copySelection,
  cutFromText,
  pasteGraphicItems as pasteGraphicItemsHelper,
  pasteIntoText,
  selectAllText,
  shouldShowAiSubtitlesItem,
  shouldShowAlignSubmenu,
  shouldShowAudioReplaceSubmenu,
  shouldShowBackgroundReplaceSubmenu,
  shouldShowEditContentItem,
  shouldShowPositionSubmenu,
  shouldShowItemReplaceSubmenu,
  shouldShowSelectLayersItem,
  shouldShowSplitItem,
  toggleMultiSelect,
} from './poster-popup-menu.helpers';
import {usePopupPosition} from './poster-popup-menu.hooks';
import styles from './poster-popup-menu.module.scss';
import {useAppSelector} from '@/hooks';

export function PosterPopupMenu(): ReactElement | null {
  const popupState = useAppSelector((state) => state.posterPopUpMenu);
  const isMultiSelectEnabled = useAppSelector((state) => state.posterEditor.posterObject?.itemsMultiSelect.enabled);
  // Canvas objects (items/backgrounds) come from the fabric selection; audio/subtitles from the timeline slice.
  const selectedTimelineItem = useAppSelector(getSelectedTimelineItem);
  const {startAiSubtitleGenerationForVideo} = useAiTranscriptGeneration();
  const activeItem = useSingleActivePosterItem();
  const activeSlide = useGetSelectedSlide();
  const isSpellCheckEnabled = getIsSpellCheckEnabledFromStore();

  const popUpRef = useRef<HTMLDivElement>(null);
  const submenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const closePopUpRef = useRef<() => void>((): void => {
    // Replaced on every render once closePopUp is defined below.
  });
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [hasWhiteboard, setHasWhiteboard] = useState(!!window.posterEditor?.whiteboard);

  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const activeCanvas = currentPage?.fabricCanvas;

  usePopupPosition(popUpRef);

  useEffect(() => {
    return (): void => {
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current);
        submenuTimeoutRef.current = null;
      }
    };
  }, []);

  // Whiteboard is created asynchronously; flip state once it appears so the
  // touch-event subscription effect below runs.
  useEffect(() => {
    if (!hasWhiteboard && window.posterEditor?.whiteboard) {
      setHasWhiteboard(true);
    }
  });

  const closePopUp = (): void => {
    setActiveSubmenu(null);
    window.PMW.redux.store.dispatch(
      updatePopUpStateAndPosition({
        openPosterPopUpMenu: false,
        popUpPosition: popupState.popUpPosition,
        target: undefined,
        eventCanvasOffset: popupState.eventCanvasOffset,
      })
    );
  };
  // Keep the latest closePopUp accessible from the stable touch handler below
  // without re-subscribing on every render.
  closePopUpRef.current = closePopUp;

  useEffect(() => {
    const whiteboard = window.posterEditor?.whiteboard;
    if (!whiteboard) {
      return undefined;
    }
    return whiteboard.on('fabricCanvas:touch:start', (): void => {
      closePopUpRef.current();
    });
  }, [hasWhiteboard]);

  // Close on any outside pointer interaction. Uses pointerdown instead of
  // mousedown so it still fires when a target calls preventDefault on
  // pointerdown (which suppresses the subsequent mousedown event).
  useEffect(() => {
    const handler = (e: PointerEvent): void => {
      const target = e.target as HTMLElement;
      if (popUpRef.current?.contains(target) || target.closest(`[${POPUP_SUBMENU_MARKER_ATTR}]`)) {
        return;
      }
      closePopUpRef.current();
    };

    document.addEventListener('pointerdown', handler, {capture: true});
    return (): void => {
      document.removeEventListener('pointerdown', handler, {capture: true});
    };
  }, []);

  if (!(activeCanvas instanceof Canvas) || !popupState.openPosterPopUpMenu) {
    return null;
  }

  const onSubmenuMouseEnter = (submenuId: string): void => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }
    submenuTimeoutRef.current = setTimeout((): void => {
      setActiveSubmenu(submenuId);
    }, SUBMENU_HOVER_DELAY_MS);
  };

  const onSubmenuMouseLeave = (): void => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }
    submenuTimeoutRef.current = setTimeout((): void => {
      setActiveSubmenu(null);
    }, SUBMENU_HOVER_DELAY_MS);
  };

  const onReplaceOption = (type: ReplaceMediaControlsButtons): void => {
    if (!activeItem || !currentPage || !(activeItem.isImage() || activeItem.isVideo())) {
      return;
    }
    closePopUp();
    onOpenReplaceMedia(type, activeItem, (itemData: ItemData): void => {
      void currentPage.items.replaceItems.replaceMedia(itemData, activeItem);
    });
  };

  const onReplaceAudioOption = (type: ReplaceMediaControlsButtons): void => {
    if (!selectedTimelineItem || !currentPage) {
      return;
    }
    const audioItemId = selectedTimelineItem.id;
    closePopUp();
    onOpenReplaceAudio(type, audioItemId);
  };

  const onReplaceBackgroundOption = (type: ReplaceMediaControlsButtons): void => {
    if (selectedTimelineItem?.type !== TimelineItemType.BACKGROUND || !currentPage) {
      return;
    }
    const bg = currentPage.items.itemsHashMap[selectedTimelineItem.id];
    if (!bg?.isBackground) {
      return;
    }
    closePopUp();
    // Backgrounds must use the dedicated background-replace path; the generic replaceMedia corrupts image backgrounds.
    onOpenReplaceBackground(type);
  };

  const onSplitItem = (): void => {
    if (!selectedTimelineItem) {
      return;
    }
    const state = window.PMW.redux.store.getState();
    const currentPageTime = getCurrentPageTime(state);
    if (!canSplitTimelineItem(selectedTimelineItem, currentPageTime)) {
      return;
    }
    void splitTimelineItem(selectedTimelineItem, currentPageTime);
    closePopUp();
  };

  const onGenerateAiSubtitlesClick = (): void => {
    if (!isUserPremium()) {
      showAiSubtitlesUpsellDialog();
      closePopUp();
      return;
    }
    if (!currentPage || !activeItem?.isVideo()) {
      return;
    }
    const videoObj = activeItem.toObject();
    const videoOrder = getVideoItemOrderInPage(activeItem.uid, currentPage);
    trackPosterBuilderGA4Events(GA4EventName.AI_SUBTITLE_CLICK_CONTEXTUAL_MENU, {[GA4EventParamName.TYPE]: GA4EventParam.VIDEO});
    void startAiSubtitleGenerationForVideo(videoObj, videoOrder);
    closePopUp();
  };

  const onEditContentClick = (): void => {
    if (activeItem?.isMenu()) {
      openEditMenuModal(activeItem);
    } else if (activeItem?.isTableItem()) {
      openEditTableContentModal(activeItem);
    }
    closePopUp();
  };

  const onSelectLayersClick = (): void => {
    openLayersPanel(LayersGA4Source.RIGHTCLICK);
    closePopUp();
  };

  const guardCtx = {target: popupState.target as Fabric.FabricObject | undefined, canvas: activeCanvas, currentPage, activeItem};

  const showPosition = shouldShowPositionSubmenu(guardCtx);
  const showItemReplace = shouldShowItemReplaceSubmenu(guardCtx);
  const showAlign = shouldShowAlignSubmenu(guardCtx);
    const showSelectLayers = shouldShowSelectLayersItem(guardCtx);
    const showAiSubtitles = shouldShowAiSubtitlesItem(guardCtx);
  const showEditContent = shouldShowEditContentItem(guardCtx);
  const showSplit = !!popupState.isTimelineItemContextMenu && shouldShowSplitItem(selectedTimelineItem);
  const showAudioReplace = !popupState.isTimelineContextMenu && shouldShowAudioReplaceSubmenu(selectedTimelineItem);
  const showBackgroundReplace = !popupState.isTimelineContextMenu && shouldShowBackgroundReplaceSubmenu(selectedTimelineItem);
  const hasTopBlock = showItemReplace || showEditContent || showAiSubtitles || showSplit || showAudioReplace || showBackgroundReplace;

  const ctx: PopupMenuCtx = {
    currentPage,
    activeItem,
    activeSlide,
    isMultiSelectEnabled,
    isSpellCheckEnabled,
    close: closePopUp,
    onCopySelection: (e): void => {
      copySelection(currentPage, e);
    },
    pasteGraphicItems: async (e): Promise<void> => {
      await pasteGraphicItemsHelper(currentPage, popupState.eventCanvasOffset, e);
    },
    onCutFromText: async (): Promise<void> => {
      await cutFromText(activeCanvas, activeItem);
    },
    onCopyFromText: async (): Promise<void> => {
      await copyFromText(activeCanvas);
    },
    onPasteText: async (e): Promise<void> => {
      await pasteIntoText(activeCanvas, activeItem, e?.nativeEvent);
    },
    onSelectAllText: (): void => {
      selectAllText(activeCanvas);
    },
    onMultiSelectClicked: (): void => {
      toggleMultiSelect(isMultiSelectEnabled);
    },
    onReplaceOption,
    onReplaceAudioOption,
    onReplaceBackgroundOption,
    onSplitItem,
    selectedTimelineItem,
    hasTopBlock,
  };

  const getMainListItems = (): ControlledListItemType[] => {
    if (popupState.isTimelineContextMenu) {
      return buildItemsForTimeline(ctx);
    }
    if (selectedTimelineItem?.type === TimelineItemType.AUDIO) {
      return buildItemsForAudioItem(ctx);
    }
    if (selectedTimelineItem?.type === TimelineItemType.BACKGROUND) {
      return buildItemsForBackgroundItem(ctx);
    }
    if (!popupState.target) {
      return buildItemsForCanvas(ctx);
    }
    if (popupState.target && activeCanvas.getActiveObjects().length > 1 && activeCanvas.getActiveObject() === popupState.target) {
      return buildItemsForGroup(ctx);
    }
    const activeObject = currentPage?.activeSelection.getActiveObject();
    if (activeObject instanceof Textbox && activeObject.isEditing) {
      return buildItemsForTextEdit(ctx, activeObject.selectionStart === activeObject.selectionEnd);
    }
    return buildItemsForSingleItem(ctx);
  };

  const submenuProps = (id: string): {id: string; isActive: boolean; onMouseEnter: () => void; onMouseLeave: () => void} => {
    return {
      id,
      isActive: activeSubmenu === id,
      onMouseEnter: (): void => {
        onSubmenuMouseEnter(id);
      },
      onMouseLeave: onSubmenuMouseLeave,
    };
  };

  const getMenuPosition = (): FabricPositionalParams => {
    return {
      left: popupState.popUpPosition?.left,
      top: popupState.popUpPosition?.top,
    };
  };

  return (
    <div ref={popUpRef} className={`spacing-p-2 ${styles.canvasPopupMenu} `} style={getMenuPosition()}>
      {showSplit ? (
        <ControlledListItemType7 id="split-item" icon="icon-split-track" text={window.i18next.t('pmwjs_split')} shortcut={makeShortcut(['S'])} onClick={onSplitItem} />
      ) : null}
      {showAudioReplace ? (
        <PopupSubmenu {...submenuProps('audio-replace')} text={window.i18next.t('pmwjs_replace')} icon="icon-replace" items={buildAudioReplaceSubMenuItems(ctx)} />
      ) : null}
      {showBackgroundReplace ? (
        <PopupSubmenu {...submenuProps('background-replace')} text={window.i18next.t('pmwjs_replace')} icon="icon-replace" items={buildBackgroundReplaceSubMenuItems(ctx)} />
      ) : null}
      {showItemReplace ? <PopupSubmenu {...submenuProps('replace')} text={window.i18next.t('pmwjs_replace')} icon="icon-replace" items={buildReplaceSubMenuItems(ctx)} /> : null}
      {showEditContent ? <ControlledListItemType7 id="edit-content" icon="icon-pencil" text={window.i18next.t('pmwjs_edit_content')} onClick={onEditContentClick} /> : null}
      {showAiSubtitles ? (
        <ControlledListItemType7
          id="generate-ai-subtitles"
          icon="icon-ai-subtitles"
          iconClasses="content-ai"
          textClasses="content-ai"
          text={window.i18next.t('pmwjs_generate_ai_subtitles')}
          onClick={onGenerateAiSubtitlesClick}
        />
      ) : null}
      {hasTopBlock && showPosition ? <hr className={styles.divider} /> : null}
      {showPosition ? (
        <PopupSubmenu {...submenuProps('position')} text={window.i18next.t('pmwjs_position')} icon="icon-prints-stack" items={buildPositionSubMenuItems(ctx)} />
      ) : null}
      {showAlign ? (
        <PopupSubmenu {...submenuProps('align')} text={window.i18next.t('pmwjs_align')} icon="icon-center-h-align-on-canvas" items={buildAlignSubMenuItems(ctx)} />
      ) : null}
      {showSelectLayers ? <hr className={styles.divider} /> : null}
      {showSelectLayers ? (
        <ControlledListItemType7 id="select-layers" icon="icon-prints-stack" text={window.i18next.t('pmwjs_select_layers')} shortcut={makeShortcut(['Shift', 'L'])} onClick={onSelectLayersClick} />
      ) : null}
      <ControlledList id="canvas-pop-up" items={getMainListItems()} />
    </div>
  );
}
